/**
 * Service layer for auth.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, inject, signal } from '@angular/core';

import { API_REPO } from '../../core/services/repositories/api-repo.token';
import { AuthUser, UpdateProfilePayload, UserLanguage } from '../../core/services/repositories/api-repository';
import { I18nService } from '../../core/i18n/i18n.service';
import { TokenService } from '../../core/services/token.service';
import { ThemeService } from '../../core/services/theme.service';

const AUTH_STORAGE_KEY = 'rd.currentUser';

/**
 * SERVICE: Auth
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly repo = inject(API_REPO);
  private readonly i18nService = inject(I18nService);
  private readonly tokenService = inject(TokenService);
  private readonly themeService = inject(ThemeService);

  readonly currentUser = signal<AuthUser | null>(null);

  /**
   * BUSINESS LOGIC: constructor
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  constructor() {
    const persistedUser = this.readPersistedUser();

    if (persistedUser) {
      this.currentUser.set(persistedUser);
      this.i18nService.useLanguage(persistedUser.language ?? 'en');
      if (persistedUser.preferredTheme) {
        this.themeService.setTheme(persistedUser.preferredTheme);
      }
    }
  }

  /**
   * BUSINESS LOGIC: login
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  login(email: string, password: string): Promise<AuthUser | null> {
    return this.repo.login({ email, password });
  }

  /**
   * BUSINESS LOGIC: changePassword
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.repo.changePassword(currentPassword, newPassword);
  }

  /**
   * BUSINESS LOGIC: hasStoredSession
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  hasStoredSession(): boolean {
    return Boolean(this.tokenService.getAccessToken() || this.tokenService.getRefreshToken());
  }

  /**
   * BUSINESS LOGIC: loadProfile
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async loadProfile(): Promise<void> {
    if (!this.hasStoredSession()) {
      return;
    }

    const profile = await this.repo.getProfile();
    this.upsertCurrentUser({ ...this.currentUser(), ...profile });

    if (profile.preferredTheme) {
      this.themeService.setTheme(profile.preferredTheme);
    }

    if (profile.preferredLanguage) {
      const lang = profile.preferredLanguage.toLowerCase() === 'fr' ? 'fr' : 'en';
      this.i18nService.useLanguage(lang);
    }
  }

  /**
   * BUSINESS LOGIC: updateProfile
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async updateProfile(payload: UpdateProfilePayload): Promise<void> {
    const updated = await this.repo.updateProfile(payload);
    this.upsertCurrentUser({ ...this.currentUser()!, ...updated });

    if (payload.preferredTheme) {
      const theme = payload.preferredTheme === 'dark' ? 'dark' : 'light';
      this.themeService.setTheme(theme);
    }

    if (payload.preferredLanguage) {
      const lang = payload.preferredLanguage.toLowerCase() === 'fr' ? 'fr' : 'en';
      this.i18nService.useLanguage(lang);
    }
  }

  /**
   * BUSINESS LOGIC: requestSignup
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  requestSignup(fullName: string, email: string, phone: string, password: string): Promise<void> {
    return this.repo.requestSignup({
      fullName,
      email,
      phone,
      password,
      preferredLanguage: this.i18nService.currentLanguage()
    });
  }

  /**
   * BUSINESS LOGIC: requestPasswordOtp
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  requestPasswordOtp(email: string): Promise<boolean> {
    return this.repo.requestPasswordOtp(email);
  }

  /**
   * BUSINESS LOGIC: verifyPasswordOtp
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  verifyPasswordOtp(email: string, otp: string): Promise<boolean> {
    return this.repo.verifyPasswordOtp(email, otp);
  }

  /**
   * BUSINESS LOGIC: resetPassword
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  resetPassword(email: string, nextPassword: string): Promise<boolean> {
    return this.repo.resetPassword(email, nextPassword);
  }

  checkUserEmailExists(email: string): Promise<{ exists: boolean; message: string }> {
    return this.repo.checkUserEmailExists(email);
  }

  /**
   * BUSINESS LOGIC: syncPreferences
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async syncPreferences(): Promise<void> {
    const preferredLanguage = localStorage.getItem('preferredLanguage') ?? undefined;
    const preferredTheme = localStorage.getItem('preferredTheme') ?? undefined;

    if (!preferredLanguage && !preferredTheme) {
      return;
    }

    try {
      await this.repo.updatePreferences({ preferredLanguage, preferredTheme });
      const profile = await this.repo.getProfile();
      this.upsertCurrentUser({ ...this.currentUser()!, ...profile });

      if (profile.preferredTheme) {
        this.themeService.setTheme(profile.preferredTheme);
      }

      if (profile.preferredLanguage) {
        const lang = profile.preferredLanguage.toLowerCase() === 'fr' ? 'fr' : 'en';
        this.i18nService.useLanguage(lang);
      }
    } catch {
      // Non-fatal — local preferences are still applied via ThemeService/i18n
    }
  }

  /**
   * BUSINESS LOGIC: updateLanguage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  updateLanguage(language: UserLanguage): void {
    const user = this.currentUser();

    if (!user) {
      return;
    }

    localStorage.setItem('preferredLanguage', language);
    this.upsertCurrentUser({ ...user, language, preferredLanguage: language.toUpperCase() });
  }

  /**
   * BUSINESS LOGIC: upsertCurrentUser
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  upsertCurrentUser(user: AuthUser): void {
    this.currentUser.set(user);
    this.i18nService.useLanguage(user.language ?? 'en');
    this.persistCurrentUser(user);
  }

  /**
   * BUSINESS LOGIC: logout
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async logout(): Promise<void> {
    const sessionId = this.tokenService.getSessionId();

    try {
      await this.repo.logout(sessionId ?? '');
    } catch {
      // Proceed with local cleanup even if the API call fails
    }

    this.clearSession();
  }

  /** Wipes local auth state immediately without calling the logout API. */
  clearSession(): void {
    this.currentUser.set(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    this.tokenService.clearTokens();
  }

  /**
   * BUSINESS LOGIC: persistCurrentUser
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private persistCurrentUser(user: AuthUser): void {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  }

  /**
   * BUSINESS LOGIC: readPersistedUser
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private readPersistedUser(): AuthUser | null {
    const rawValue = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as AuthUser;

      if (!parsed || typeof parsed.email !== 'string' || typeof parsed.role !== 'string' || typeof parsed.name !== 'string') {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return null;
      }

      return parsed;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  }
}
